import { flow, getEnv, types } from "mobx-state-tree";

import { Column, ColumnType } from "../../components/datatable/interfaces";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { endpoints } from "../api/ApiEndpoints";
import { Inspection } from "./Inspection";
import {
  InspectionDataGridResponseWithDynamicProperties,
  HeaderItemResponse,
  InspectionRowResponse,
} from "../../api-models/response/DataGridResponse";
import { moveItemInArray } from "../../helpers/ArrayHelpers";
import { DATUM_FIELD } from "../../api-models/response/BluemetricFields";

const esicTableColumns: Column[] = [
  {
    key: "Inspection",
    name: "general.inspection",
    type: ColumnType.String,
    flex: 2,
  },
  {
    key: "Modified",
    name: "general.last-edit",
    type: ColumnType.DateTime,
    sortable: true,
    filterable: true,
  },
  {
    key: "IsRelevant",
    name: "general.inspections.list-header.relevance",
    type: ColumnType.Boolean,
    sortable: true,
    filterable: true,
  },
];

const InspectionsFilter = types.model({
  sectionId: types.number,
});

export const InspectionsStorageModel = types
  .compose(InspectionsFilter, DataGridStorageModel)
  .named("InspectionsStorageModel")
  .actions((self) => {
    const getHeaders = (response: InspectionDataGridResponseWithDynamicProperties): Column[] => {
      const tableHeaders = response.header as HeaderItemResponse[];

      const BMColumns = tableHeaders
        .filter((header) => esicTableColumns.every((x) => x.key !== header.key))
        .map((column: HeaderItemResponse) => {
          return {
            key: column.key,
            name: column.name,
            type: self.getColumnType(column.metadata),
            valueOptions: self.getColumnPredefinedValueOptions(column.metadata),
            sortable: true,
            filterable: true,
          };
        });

      const allColumns = esicTableColumns.concat(BMColumns);

      const modifiedColumnIndex = allColumns.findIndex((x) => x.key === "Modified");
      moveItemInArray(allColumns, modifiedColumnIndex, tableHeaders.length - 1);

      return allColumns;
    };
    return {
      afterCreate() {
        self.defaultSort = { field: DATUM_FIELD, sort: "desc" };
      },
      mapItem(item: InspectionRowResponse) {
        return new Inspection(item);
      },
      mapResponse(response: InspectionDataGridResponseWithDynamicProperties) {
        self.data = {
          totalCount: response.totalCount,
          items: response.rows.map(self.mapItem),
        };
        self.gridColumns = getHeaders(response);
      },
      deleteInspections: flow(function* deleteInspections(inspectionIds: number[]) {
        const endpoint = endpoints.get("inspections");
        const response = yield getEnv(self).api.delete(endpoint, inspectionIds);
        self.deselectAll();
        yield self.load();
        return response;
      }),
      validateIfInspectionsCanBeDeleted: flow(function* (inspectionIds: number[]) {
        const endpoint = endpoints.get("verify_inspections_can_be_deleted");

        return yield getEnv(self).api.post(endpoint, inspectionIds);
      }),
    };
  })
  .views((self) => ({
    get canLoadItems() {
      return !!self.sectionId;
    },
    get endpoint() {
      const endpoint = endpoints.get("inspections_all");
      endpoint.queryParams = `sewerObjectId=${self.sectionId}&${self.getDataQuery()}`;
      return endpoint;
    },
  }));
